import React, { useMemo, useCallback, useId } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useTheme } from "@mui/material/styles";
import "./InputFields.css";

/** Strip Emotion/nested keys so merged styles are valid for React `style` */
function sanitizeReactInlineStyle(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  const out = {};
  for (const k of Object.keys(obj)) {
    if (k === "label" || k.startsWith("&") || k.startsWith(":")) continue;
    const v = obj[k];
    if (v == null) continue;
    const t = typeof v;
    if (t === "string" || t === "number") out[k] = v;
  }
  return out;
}

const INPUT_TYPO = {
  fontSize: 15,
  lineHeight: "1.4",
  fontFamily: "inherit",
};

function buildStyles(fieldHeight, hasError, isDark = false) {
  const INPUT_BG = isDark ? "#1e2a3d" : "#F7F8FA";
  const INPUT_BG_FOCUS = isDark ? "#151d2a" : "#FFFFFF";
  const INPUT_BORDER_DEFAULT = isDark ? "#2d3b4e" : "#E5E7EB";
  const INPUT_BORDER_HOVER = isDark ? "#3d4f66" : "#D1D5DB";
  const INPUT_BORDER_FOCUS = hasError
    ? "#dc3545"
    : isDark
      ? "#60a5fa"
      : "#2563EB";
  const INPUT_TEXT = isDark ? "#e8eaed" : "#374151";
  const INPUT_PLACEHOLDER = hasError
    ? "#dc3545"
    : isDark
      ? "#9aa0a6"
      : "#9ca3af";
  const INPUT_SINGLE_MUTED = isDark ? "#9aa0a6" : "#4b5563";
  const MENU_BG = isDark ? "#1a2332" : "#ffffff";
  const MENU_BORDER = isDark ? "#2d3b4e" : "rgba(226, 232, 240, 0.95)";
  const OPTION_SELECTED_BG = isDark
    ? "rgba(96, 165, 250, 0.22)"
    : "#eef2f6";
  const OPTION_FOCUS_BG = isDark
    ? "rgba(96, 165, 250, 0.12)"
    : "#f1f5f9";

  const border = hasError ? "#dc3545" : INPUT_BORDER_DEFAULT;
  const borderHover = hasError ? "#dc3545" : INPUT_BORDER_HOVER;
  const borderFocus = hasError ? "#dc3545" : INPUT_BORDER_FOCUS;
  const focusRing = hasError
    ? "var(--focus-ring-error)"
    : isDark
      ? "0 0 0 3px rgba(96, 165, 250, 0.22)"
      : "0 0 0 3px rgba(37, 99, 235, 0.1)";

  return {
    container: (base) => ({
      ...base,
      margin: 0,
      width: "100%",
    }),
    /* Outer box — match .cs-input-shell height (border-box) */
    control: (base, state) => ({
      ...base,
      minHeight: fieldHeight,
      height: fieldHeight,
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "stretch",
      boxSizing: "border-box",
      borderRadius: 8,
      borderColor: state.isFocused ? borderFocus : border,
      borderWidth: 1,
      boxShadow: state.isFocused ? focusRing : "none",
      backgroundColor: state.isDisabled
        ? isDark
          ? "#151d2a"
          : "#e2e8f0"
        : state.isFocused
          ? INPUT_BG_FOCUS
          : INPUT_BG,
      "&:hover": {
        borderColor: state.isDisabled
          ? border
          : state.isFocused
            ? borderFocus
            : borderHover,
      },
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.72 : 1,
    }),
    /*
     * v5 uses display:grid on ValueContainer for single-select (placeholder + input overlap).
     * Do NOT force flex or a fixed height here — that breaks vertical centering vs InputText.
     */
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px 0 14px",
      alignItems: "center",
      alignContent: "center",
      alignSelf: "stretch",
      flex: "1 1 0%",
      minWidth: 0,
      minHeight: 0,
      height: "100%",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: INPUT_SINGLE_MUTED,
      ...INPUT_TYPO,
    }),
    placeholder: (base) => ({
      ...base,
      color: INPUT_PLACEHOLDER,
      ...INPUT_TYPO,
      margin: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: hasError ? "#dc3545" : INPUT_TEXT,
      ...INPUT_TYPO,
      margin: 0,
      fontWeight: 500,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      alignSelf: "stretch",
      alignItems: "center",
      flexShrink: 0,
      flexGrow: 0,
      paddingRight: 4,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: INPUT_SINGLE_MUTED,
      padding: "0 10px",
      height: "100%",
      display: "flex",
      alignItems: "center",
      alignSelf: "stretch",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "0 6px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      border: `1px solid ${MENU_BORDER}`,
      boxShadow: isDark
        ? "0 12px 40px rgba(0, 0, 0, 0.45), 0 2px 10px rgba(0, 0, 0, 0.3)"
        : "0 12px 40px rgba(15, 23, 42, 0.14), 0 2px 10px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      zIndex: 20000,
      backgroundColor: MENU_BG,
      marginTop: 4,
      marginBottom: 4,
    }),
    menuList: (base) => ({
      ...base,
      padding: 6,
      maxHeight: 300,
      backgroundColor: MENU_BG,
    }),
    option: (base, state) => {
      const focused = state.isFocused && !state.isDisabled;
      const selected = state.isSelected;
      return {
        ...base,
        borderRadius: 8,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        fontSize: 15,
        lineHeight: 1.45,
        padding: "10px 14px",
        color: INPUT_TEXT,
        fontWeight: selected ? 600 : 400,
        backgroundColor: selected
          ? OPTION_SELECTED_BG
          : focused
            ? OPTION_FOCUS_BG
            : "transparent",
        opacity: state.isDisabled ? 0.55 : 1,
      };
    },
    menuPortal: (base) => ({ ...base, zIndex: 20000 }),
  };
}

/** Merge user react-select style overrides onto base (Materials & Labor, etc.) */
export function mergeReactSelectStyles(baseStyles, userStyles) {
  if (!userStyles || typeof userStyles !== "object") return baseStyles;
  const merged = { ...baseStyles };
  for (const key of Object.keys(userStyles)) {
    const userFn = userStyles[key];
    const baseFn = merged[key];
    if (typeof userFn === "function") {
      merged[key] = (base, state) =>
        userFn(baseFn ? baseFn(base, state) : base, state);
    }
  }
  return merged;
}

export function buildInputDropdownStyles(fieldHeight, hasError, isDark = false) {
  return buildStyles(fieldHeight, hasError, isDark);
}

/**
 * Shared react-select field: flat `label` (use with `required` for *), `cs-rs` chrome (§7).
 * Use `ariaLabel` when `label` is intentionally empty (toolbars) so assistive tech still has a name.
 */
const InputDropdown = ({
  options = [],
  value,
  onChange,
  label,
  name,
  placeholder,
  onTextFieldChange,
  onBlur,
  error,
  errorBorderOnly = false,
  helperText,
  type,
  textFieldProps = {},
  inputValue: controlledInputValue,
  getOptionLabel = (o) => {
    if (o == null) return "";
    if (typeof o === "string" || typeof o === "number") return String(o);
    return o?.label ?? o?.name ?? o?.Name ?? String(o);
  },
  getOptionDisabled,
  filterOptions,
  renderOption,
  showErrorOnly,
  fieldHeight = "44px",
  freeSolo,
  disableClearable,
  autoSelect,
  loading,
  onKeyDown,
  disabled,
  disable,
  required,
  /** When `label` is empty (toolbar / compact), set for screen readers */
  ariaLabel,
  /** Merged onto default react-select `styles` (e.g. line-item grouped select) */
  styles: reactSelectStylesMerge,
  /** Merged onto default `components` (custom Option, NoOptionsMessage, …) */
  components: reactSelectComponentsMerge,
  /** Spread onto react-select after internal props — use for `menuIsOpen`, `classNamePrefix`, etc. */
  selectProps = {},
  /** Extra class on the outer `.cs-rs__wrapper` */
  className: wrapperClassName,
  noOptionsText,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const uid = useId();
  const hasError = Boolean(error);
  // Project-wide rule: never surface inline error text below a field — the
  // red border/shell is the only error affordance. Hint helperText (when
  // there is no error) still renders for fields that need guidance copy.
  const displayHelperText = hasError || showErrorOnly ? undefined : helperText;

  const {
    sx: _sx,
    className: tfClassName,
    inputProps: tfInputProps,
    InputLabelProps: _ilp,
    ...restTextField
  } = textFieldProps;

  const isDisabled = Boolean(
    disable || disabled || restTextField?.disabled
  );

  let displayLabel = label;
  let effectiveRequired = Boolean(required);
  if (typeof label === "string") {
    const stripped = label.replace(/\s*\*\s*$/, "");
    if (stripped !== label) {
      displayLabel = stripped;
      effectiveRequired = true;
    }
  }

  const [internalInput, setInternalInput] = React.useState("");

  const inputValueControlled = controlledInputValue !== undefined;

  const effectiveInputValue = inputValueControlled
    ? controlledInputValue == null
      ? ""
      : String(controlledInputValue)
    : internalInput;

  const filteredOptions = useMemo(() => {
    const opts = options || [];
    if (!filterOptions) return opts;
    try {
      return filterOptions(opts, {
        inputValue: effectiveInputValue,
      });
    } catch {
      return opts;
    }
  }, [options, filterOptions, effectiveInputValue]);

  const wrapPrimitive = useCallback(
    (o) => {
      if (o == null) return null;
      if (typeof o === "string" || typeof o === "number") {
        return {
          __prim: true,
          raw: o,
          label: getOptionLabel(o),
        };
      }
      return o;
    },
    [getOptionLabel]
  );

  const rsOptions = useMemo(() => {
    const opts = filteredOptions || [];
    if (!opts.length) return [];
    const first = opts[0];
    const isGrouped =
      first &&
      typeof first === "object" &&
      first.options != null &&
      Array.isArray(first.options);
    if (isGrouped) {
      return opts.map((g) => ({
        ...g,
        options: (g.options || []).map((o) => wrapPrimitive(o)),
      }));
    }
    return opts.map((o) => wrapPrimitive(o));
  }, [filteredOptions, wrapPrimitive]);

  const selectValue = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    return wrapPrimitive(value);
  }, [value, wrapPrimitive]);

  /* Controlled `inputValue` + a selected `value` forces the search box to match the label
   * on every render, so backspace/clear does not update instantly (Address State/Country,
   * Worker/Contract, recurring frequency, POS customer search, etc.). Only forward
   * `inputValue` when there is no selection — e.g. pure search before pick, or empty. */
  const forwardInputValueToSelect =
    inputValueControlled &&
    selectValue == null &&
    !(
      controlledInputValue === "" || controlledInputValue == null
    );

  const unwrapOption = (opt) => {
    if (opt == null) return null;
    if (opt.__prim) return opt.raw;
    return opt;
  };

  const handleChange = useCallback(
    (opt) => {
      const next = opt === undefined ? null : unwrapOption(opt);
      onChange?.(null, next);
    },
    [onChange]
  );

  const handleCreate = useCallback(
    (inputVal) => {
      onChange?.(null, inputVal);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (val, meta) => {
      if (meta.action !== "input-change") {
        return;
      }
      if (!inputValueControlled) {
        setInternalInput(val ?? "");
      }
      const ev = {
        target: {
          value: val ?? "",
          name: name || tfInputProps?.name,
        },
        currentTarget: {
          value: val ?? "",
          name: name || tfInputProps?.name,
        },
      };
      onTextFieldChange?.(ev, val ?? "");
    },
    [inputValueControlled, name, onTextFieldChange, tfInputProps?.name]
  );

  const CustomOption = useMemo(() => {
    if (!renderOption) return undefined;
    return function OptionWrap(props) {
      const { data, innerProps, innerRef, getStyles, isFocused, isSelected } =
        props;
      const raw = data && data.__prim ? data.raw : data;
      /* Critical: default Option applies getStyles('option') — custom renderOption must merge or hover/selected look "unstyled" vs Industry (no renderOption). */
      let optionStyles = {};
      if (typeof getStyles === "function") {
        try {
          optionStyles = getStyles("option", props) || {};
        } catch {
          optionStyles = {};
        }
      }
      const mergedStyle = {
        ...sanitizeReactInlineStyle(optionStyles),
        ...sanitizeReactInlineStyle(innerProps.style || {}),
      };
      const liProps = {
        ...innerProps,
        ref: innerRef,
        role: "option",
        style: mergedStyle,
        className: [innerProps.className, isFocused ? "cs-rs-option--focused" : ""]
          .filter(Boolean)
          .join(" "),
        "aria-selected": isSelected,
      };
      return renderOption(liProps, raw);
    };
  }, [renderOption]);

  const styles = useMemo(
    () =>
      mergeReactSelectStyles(
        buildStyles(fieldHeight, hasError && !errorBorderOnly, isDarkMode),
        reactSelectStylesMerge
      ),
    [fieldHeight, hasError, errorBorderOnly, reactSelectStylesMerge, isDarkMode]
  );

  const SelectComp = freeSolo === true ? CreatableSelect : Select;

  const isClearable = disableClearable !== true;

  const getOptionLabelRs = useCallback(
    (o) => {
      if (o && o.__prim) return o.label;
      return getOptionLabel(o);
    },
    [getOptionLabel]
  );

  const getOptionValueRs = useCallback((o) => {
    if (o == null) return "";
    if (o.__prim) return o.raw;
    if (typeof o === "object") {
      /* Prefer app ids so react-select can match value ↔ options (POS customer uses UserId) */
      if (o.value !== undefined && o.value !== null && o.value !== "") return o.value;
      if (o.UserId !== undefined && o.UserId !== null && o.UserId !== "")
        return String(o.UserId);
      if (o.CustomerId !== undefined && o.CustomerId !== null && o.CustomerId !== "")
        return String(o.CustomerId);
      if (o._id !== undefined && o._id !== null) return String(o._id);
      if (o.id !== undefined && o.id !== null) return o.id;
      if (o.code !== undefined) return o.code;
    }
    return o;
  }, []);

  const handleBlurRs = useCallback(
    (e) => {
      onBlur?.({
        ...e,
        target: {
          ...e.target,
          name: name || textFieldProps?.name,
        },
      });
    },
    [onBlur, name, textFieldProps?.name]
  );

  const derivedPlaceholder = displayLabel
    ? `Select ${String(displayLabel).replace(/^Select\s+/i, "").trim()}`
    : "";
  const effectiveDropdownPlaceholder = placeholder ?? derivedPlaceholder;

  const common = {
    instanceId: uid,
    id: textFieldProps?.id,
    name: name || textFieldProps?.name,
    options: rsOptions,
    value: selectValue,
    onChange: handleChange,
    onBlur: handleBlurRs,
    getOptionLabel: getOptionLabelRs,
    getOptionValue: getOptionValueRs,
    placeholder: effectiveDropdownPlaceholder,
    isDisabled,
    isClearable,
    isSearchable: true,
    isOptionDisabled: getOptionDisabled
      ? (opt) => getOptionDisabled(opt && opt.__prim ? opt.raw : opt)
      : undefined,
    filterOption: filterOptions ? () => true : undefined,
    styles,
    classNamePrefix: "cs-rs",
    menuPlacement: "auto",
    menuPortalTarget:
      typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    components: {
      IndicatorSeparator: () => null,
      ...(CustomOption ? { Option: CustomOption } : {}),
      ...(reactSelectComponentsMerge || {}),
    },
    onInputChange: handleInputChange,
    noOptionsMessage: () => noOptionsText || "No matching item",
    isLoading: Boolean(loading),
    onKeyDown,
    ...(freeSolo === true ? { onCreateOption: handleCreate } : {}),
    ...(forwardInputValueToSelect ? { inputValue: effectiveInputValue } : {}),
  };

  /* selectProps last so `classNamePrefix`, `menuIsOpen`, etc. can override defaults */
  const selectMerged = { ...common, ...selectProps };

  return (
    <div
      className={["cs-rs__wrapper", tfClassName || "", wrapperClassName || ""]
        .filter(Boolean)
        .join(" ")}
      aria-required={effectiveRequired ? true : undefined}
      aria-label={!label && ariaLabel ? ariaLabel : undefined}
    >
      {displayLabel ? (
        <label
          className={`cs-rs__label ${hasError && !errorBorderOnly ? "cs-field-label--error" : ""}`}
          htmlFor={textFieldProps?.id}
        >
          {displayLabel}
          {effectiveRequired ? (
            <span className="cs-field-label-required" aria-hidden="true">
              {" "}
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <SelectComp {...selectMerged} />
      {hasError && displayHelperText ? (
        <div className="cs-field-helper cs-field-helper--error" role="alert">
          {displayHelperText}
        </div>
      ) : displayHelperText ? (
        <div className="cs-field-helper">{displayHelperText}</div>
      ) : null}
    </div>
  );
};

export default InputDropdown;
